"use client";

import * as React from "react";

/**
 * Whether a trailing adornment has room in its row WITHOUT costing its
 * neighbors any width — the yield rule for a chip that rides beside a
 * truncating title: the title owns the row, and the chip drops to its
 * compact form (an icon) the moment its full form would push the title into
 * an ellipsis.
 *
 * Attach `ref` to the adornment. Its parent is the row (a flex row with a
 * `gap`); every other child of that row is a neighbor. The adornment fits
 * when the row can hold each neighbor at its NATURAL width — the width it
 * would take unclipped, recovered from any ellipsizing descendant's
 * `scrollWidth` — plus the gaps, plus the adornment at its full width.
 *
 * The full width is captured whenever the adornment renders in its full
 * form, which it does first (`fits` starts `true`), so the compact form
 * always knows what it is standing in for. Re-measures on every resize of
 * the row or a neighbor: a shorter title is a neighbor resize; a longer one
 * that is already clipped changes nothing the adornment can use, and the
 * answer stays `false`.
 *
 * Stable by construction: the row's width is parent-driven, and a neighbor
 * that widens because the adornment shrank has the same natural width as
 * before, so flipping `fits` never flips it back.
 */
export function useFitsBeside<T extends HTMLElement = HTMLElement>(): {
  ref: React.RefCallback<T> & React.RefObject<T | null>;
  fits: boolean;
} {
  const [node, setNode] = React.useState<T | null>(null);
  const [fits, setFits] = React.useState(true);
  const fitsRef = React.useRef(true);
  const fullWidthRef = React.useRef(0);

  // Stable callback ref (see `useElementSize`): the adornment mounts inside
  // overlays that render no subtree while closed.
  const ref = React.useMemo(() => {
    const attach = ((el: T | null) => {
      attach.current = el;
      setNode(el);
    }) as React.RefCallback<T> & { current: T | null };
    attach.current = null;
    return attach;
  }, []);

  React.useLayoutEffect(() => {
    if (!node) {
      // Detached (overlay closed): reset so the next mount renders full first
      // and captures its own width.
      fitsRef.current = true;
      fullWidthRef.current = 0;
      setFits(true);
      return;
    }
    // A React ref always attaches to a mounted element, which has a parent.
    const row = node.parentElement as HTMLElement;
    const measure = () => {
      if (fitsRef.current) fullWidthRef.current = node.offsetWidth;
      const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0;
      const neighbors = Array.from(row.children).filter(
        (child) => child !== node,
      ) as HTMLElement[];
      const needed =
        neighbors.reduce((sum, child) => sum + naturalWidth(child), 0) +
        gap * neighbors.length +
        fullWidthRef.current;
      const next = needed <= row.clientWidth;
      if (next === fitsRef.current) return;
      fitsRef.current = next;
      setFits(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    for (const child of Array.from(row.children)) {
      if (child !== node) observer.observe(child);
    }
    return () => observer.disconnect();
  }, [node]);

  return { ref, fits };
}

/**
 * The width an element would take unclipped: its laid-out width plus the
 * widest overflow any descendant hides (`scrollWidth − clientWidth` of an
 * ellipsizing span). The max, not the sum — nested clippers along one path
 * hide the same text. `> 1` absorbs subpixel rounding between the two
 * integer measures.
 */
function naturalWidth(el: HTMLElement): number {
  let hidden = 0;
  const walk = (node: Element) => {
    const extra = node.scrollWidth - node.clientWidth;
    if (extra > 1 && extra > hidden) hidden = extra;
    for (const child of Array.from(node.children)) walk(child);
  };
  walk(el);
  return el.offsetWidth + hidden;
}
